import { Boxes, Ticket, Network, Users } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "@/lib/scroll";

const UTILITIES = [
    {
        t: "Ecosystem utility",
        d: "Designed to power participation across TAVONX products and services.",
        icon: Boxes,
        tid: "tvx-utility-ecosystem",
    },
    {
        t: "Platform participation",
        d: "Intended as part of how users engage with experiences across the platform.",
        icon: Ticket,
        tid: "tvx-utility-participation",
    },
    {
        t: "Future ecosystem integrations",
        d: "Planned for use as the TAVONX ecosystem expands and evolves.",
        icon: Network,
        tid: "tvx-utility-integrations",
    },
    {
        t: "Community-focused utility",
        d: "Built with the TAVONX community at the centre of the ecosystem.",
        icon: Users,
        tid: "tvx-utility-community",
    },
];

const NODES = [
    { t: "Trade", pos: "left-[2%] top-[16%]", x: 12, y: 22, tid: "tvx-node-trade" },
    { t: "Ecosystem", pos: "right-[2%] top-[30%]", x: 88, y: 34, tid: "tvx-node-ecosystem" },
    { t: "Utility", pos: "right-[6%] bottom-[12%]", x: 80, y: 84, tid: "tvx-node-utility" },
    { t: "Participation", pos: "left-[6%] bottom-[20%]", x: 18, y: 76, tid: "tvx-node-participation" },
];

export default function TokenSection() {
    return (
        <section id="tvx" data-testid="tvx-token-section" className="relative overflow-hidden bg-base py-24 lg:py-32">
            <div className="pointer-events-none absolute left-[-12%] top-[10%] h-[55vh] w-[45vw] rounded-full bg-[radial-gradient(circle,rgba(139,92,246,0.07),transparent_70%)]" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-12">
                    {/* orbit visual */}
                    <Reveal className="order-2 flex justify-center lg:order-1">
                        <div
                            data-testid="tvx-orbit-visual"
                            className="relative h-[min(400px,calc(100vw-2.5rem))] w-[min(400px,calc(100vw-2.5rem))] sm:h-[480px] sm:w-[480px] lg:h-[540px] lg:w-[540px]"
                        >
                            <div className="absolute inset-[14%] animate-halo rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.26)_0%,rgba(139,92,246,0.12)_55%,transparent_78%)]" />

                            <div className="absolute inset-[10%] rounded-full border border-white/[0.06]" />
                            <div className="absolute inset-0 rounded-full border border-cyan-400/15">
                                <span className="absolute left-1/2 top-0 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-cyber shadow-[0_0_12px_rgba(0,209,255,0.9)]" />
                            </div>

                            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                                <defs>
                                    <linearGradient id="nodeline" x1="0" y1="0" x2="1" y2="1">
                                        <stop offset="0%" stopColor="#00D1FF" stopOpacity="0.7" />
                                        <stop offset="100%" stopColor="#00D1FF" stopOpacity="0.05" />
                                    </linearGradient>
                                </defs>
                                {NODES.map((n) => (
                                    <line
                                        key={n.t}
                                        x1="50"
                                        y1="50"
                                        x2={n.x}
                                        y2={n.y}
                                        stroke="url(#nodeline)"
                                        strokeWidth="0.35"
                                    />
                                ))}
                            </svg>

                            <div className="absolute inset-0 m-auto w-[54%] -rotate-[8deg]">
                                <img
                                    src={`${process.env.PUBLIC_URL}/brand/tvx-coin-hero.png`}
                                    alt="TNX — TAVONX Utility Token"
                                    loading="lazy"
                                    draggable={false}
                                    data-testid="tvx-coin-visual"
                                    className="w-full animate-float-y select-none drop-shadow-[0_0_60px_rgba(0,209,255,0.4)]"
                                />
                            </div>

                            {NODES.map((n, i) => (
                                <motion.div
                                    key={n.t}
                                    data-testid={n.tid}
                                    className={`absolute z-10 ${n.pos}`}
                                    initial={{ opacity: 0, scale: 0.85 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true, amount: 0.4 }}
                                    transition={{ duration: 0.6, delay: 0.3 + i * 0.12 }}
                                >
                                    <div
                                        className="glass flex animate-float-y items-center gap-2 rounded-md px-3 py-2"
                                        style={{ animationDelay: `${i * 0.9}s` }}
                                    >
                                        <span className="h-1.5 w-1.5 rounded-full bg-cyber shadow-[0_0_8px_rgba(0,209,255,0.9)]" />
                                        <span className="font-grotesk text-[11px] font-semibold uppercase tracking-[0.2em] text-white">
                                            {n.t}
                                        </span>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </Reveal>

                    {/* copy + utility points */}
                    <div className="order-1 lg:order-2">
                        <Reveal>
                            <Eyebrow>TNX Token</Eyebrow>
                        </Reveal>
                        <Reveal delay={0.08}>
                            <h2 className="font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                                Meet <span className="text-grad-blue">TNX.</span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.14}>
                            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-400">
                                The utility token at the heart of the TAVONX ecosystem.
                            </p>
                        </Reveal>

                        <div className="mt-9 grid gap-3 sm:grid-cols-2">
                            {UTILITIES.map((u, i) => (
                                <Reveal key={u.t} delay={0.1 + i * 0.06}>
                                    <div
                                        data-testid={u.tid}
                                        className="group h-full rounded-lg border border-white/[0.07] bg-panel/40 p-5 transition-all duration-500 hover:border-cyber/40 hover:bg-panel/70"
                                    >
                                        <u.icon className="h-[18px] w-[18px] text-cyber" strokeWidth={1.5} />
                                        <h3 className="mt-4 font-grotesk text-[13px] font-semibold uppercase tracking-[0.14em] text-white">
                                            {u.t}
                                        </h3>
                                        <p className="mt-2 text-xs leading-relaxed text-slate-400">{u.d}</p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>

                        <Reveal delay={0.3}>
                            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                                <a
                                    href="#ecosystem"
                                    onClick={scrollToId("ecosystem")}
                                    data-testid="tvx-cta-explore-token"
                                    className="rounded bg-gradient-to-r from-electric to-cyber px-8 py-3.5 font-grotesk text-xs font-semibold uppercase tracking-[0.2em] text-void transition-all duration-300 hover:shadow-[0_0_32px_rgba(0,209,255,0.35)]"
                                >
                                    Explore TNX
                                </a>
                                <p className="max-w-xs text-[11px] leading-relaxed text-slate-600">
                                    TNX is a utility token. Nothing on this page is financial
                                    advice or a promise of future performance.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
